import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/react";
import { dark } from "@clerk/ui/themes";

const appearance = {
  theme: dark,
  captcha: { theme: "dark" },
  variables: {
    colorPrimary: "#facc15",
    colorPrimaryForeground: "#0a0a0c",
    colorBackground: "#141419",
    colorInput: "#1e1e26",
    fontFamily: "Outfit, system-ui, sans-serif",
    borderRadius: "0.4rem",
  },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClerkAuthProvider({ children }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Retry with backoff for ~3 minutes: a publish/restart can briefly make the server unreachable.
    const load = async (attempt = 0) => {
      try {
        const cfg = await (await fetch(`${BACKEND_URL}/api/clerk/config`)).json();
        if (cfg.enabled) { setUnavailable(false); return cancelled || setConfig(cfg); }
      } catch (e) {
        console.warn("Sign-in config not ready yet, retrying", e); // backend may still be starting
      }
      if (cancelled) return;
      if (attempt >= 3) setUnavailable(true); // show a calm "starting up" note, but keep trying
      if (attempt >= 40) return setGaveUp(true);
      setTimeout(() => load(attempt + 1), Math.min(1500 * 1.5 ** Math.min(attempt, 4), 6000));
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (!config && unavailable) return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#0a0a0c", color: "#d4d4d8", fontFamily: "Outfit, system-ui, sans-serif", textAlign: "center", padding: 24 }}
      data-testid="signin-starting-up">
      <div>
        {!gaveUp && <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-zinc-700 border-t-yellow-400" />}
        <p style={{ fontSize: 16 }}>{gaveUp ? "Bandcamp still can't connect." : "Bandcamp is starting up — this page will connect by itself in a moment."}</p>
        {gaveUp && (
          <button onClick={() => window.location.reload()} data-testid="signin-reload-btn"
            style={{ marginTop: 16, background: "#facc15", color: "#0a0a0c", padding: "8px 18px", borderRadius: 4, fontWeight: 700 }}>Reload</button>
        )}
      </div>
    </div>
  );
  if (!config) return null;
  return (
    <ClerkProvider
      publishableKey={config.publishable_key}
      proxyUrl={config.proxy_url || undefined}
      appearance={appearance}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/"
    >
      {children}
    </ClerkProvider>
  );
}
